import type { ICourse, ICourseDay } from '@/data/courses';
import type { IExercise } from '@/data/exercises';
import type { ExerciseConfiguration } from '@/lib/memberTrainingDay';

export interface WorkoutSource {
  course: Pick<ICourse, 'id' | 'name'>;
  day: Pick<ICourseDay, 'id' | 'name' | 'durationMin' | 'warmupIds' | 'mainIds' | 'stretchIds'> & { orderedIds?: string[] };
}

export function workoutExerciseIds(source: WorkoutSource | null): string[] {
  if (!source) return [];
  return source.day.orderedIds ?? [...source.day.warmupIds, ...source.day.mainIds, ...source.day.stretchIds];
}

/** URL selection is an execution view; it never changes a known course's identity. */
export function resolveWorkoutSource({ dayId, courses, customExercises, customOrdered, singleExercise, memberSource }: {
  dayId?: string;
  courses: ICourse[];
  customExercises: IExercise[];
  customOrdered: boolean;
  singleExercise: IExercise | null;
  memberSource: WorkoutSource | null;
}): WorkoutSource | null {
  if (dayId?.startsWith('member-')) return memberSource;
  for (const course of courses) {
    const day = course.days.find(candidate => candidate.id === dayId);
    if (!day) continue;
    if (!customExercises.length) return { course, day };
    const allowed = [...day.warmupIds, ...day.mainIds, ...day.stretchIds];
    // Keep repeated occurrences and exact teacher/legacy IDs, in their requested order.
    const chosen = customExercises.map(exercise => exercise.id).filter(id => allowed.includes(id));
    return { course, day: { ...day,
      warmupIds: customOrdered ? [] : chosen.filter(id => day.warmupIds.includes(id)),
      mainIds: customOrdered ? chosen : chosen.filter(id => day.mainIds.includes(id)),
      stretchIds: customOrdered ? [] : chosen.filter(id => day.stretchIds.includes(id)),
      orderedIds: customOrdered ? chosen : undefined,
    } };
  }
  if (customExercises.length) {
    const warmupIds = customOrdered ? [] : customExercises.filter(ex => ex.bodyPart === 'warmup').map(ex => ex.id);
    const stretchIds = customOrdered ? [] : customExercises.filter(ex => ex.bodyPart === 'stretch').map(ex => ex.id);
    const mainIds = customOrdered ? customExercises.map(ex => ex.id)
      : customExercises.filter(ex => ex.bodyPart !== 'warmup' && ex.bodyPart !== 'stretch').map(ex => ex.id);
    const duration = customExercises.reduce((sum, ex) => sum + ex.sets * (ex.restSeconds + 45) / 60, 0);
    return { course: { id: 'custom-workout', name: '自由搭配训练' },
      day: { id: 'custom-workout', name: '自由搭配训练', warmupIds, mainIds, stretchIds,
        orderedIds: customOrdered ? customExercises.map(ex => ex.id) : undefined,
        durationMin: Math.max(5, Math.round(duration)) } };
  }
  if (singleExercise) {
    const warmup = singleExercise.bodyPart === 'warmup', stretch = singleExercise.bodyPart === 'stretch';
    return { course: { id: 'warmup-single', name: warmup ? '热身激活' : stretch ? '拉伸放松' : '单动作训练' },
      day: { id: `single-${singleExercise.id}`, name: singleExercise.name, durationMin: 5,
        warmupIds: warmup ? [singleExercise.id] : [], mainIds: !warmup && !stretch ? [singleExercise.id] : [],
        stretchIds: stretch ? [singleExercise.id] : [] } };
  }
  // Preserve the existing no-match fallback; invalid-link policy is a separate reported issue.
  const course = courses.find(candidate => candidate.system === 'three-way');
  return course?.days[0] ? { course, day: course.days[0] } : null;
}

export function parseWorkoutConfigurations(ids: string | null, settings: string | null,
  fallback: Record<string, ExerciseConfiguration>): Record<string, ExerciseConfiguration> {
  if (!ids || !settings) return fallback;
  const configurations: Record<string, ExerciseConfiguration> = {};
  const parts = settings.split('|');
  ids.split(',').filter(Boolean).forEach((id, index) => {
    const segments = parts[index]?.split('x');
    if (!segments || segments.length < 3) return;
    const rest = parseInt(segments[segments.length - 1]);
    configurations[id] = { sets: parseInt(segments[0]) || 3, reps: segments.slice(1, -1).join('x'),
      restSeconds: Number.isFinite(rest) ? Math.max(0, rest) : 60 };
  });
  return configurations;
}
